<template>
  <VaLayout v-if="breakpoint.lgUp" class="h-screen bg-[var(--va-background-secondary)]">
    <template #left>
      <RouterLink
        class="h-full flex items-center justify-center relative"
        :style="leftPanelStyle"
        to="/"
        aria-label="Visit homepage"
      >
        <div v-if="loginBannerUrl" class="absolute inset-0 w-full h-full" style="overflow: hidden">
          <img
            :src="loginBannerUrl"
            alt="Login Banner"
            style="width: 100%; height: 100%; object-fit: cover; object-position: center; display: block"
            @error="handleBannerError"
          />
        </div>
        <!-- Only show logo if no banner is present -->
        <div v-if="!loginBannerUrl" class="relative z-10">
          <VuesticLogo :height="112" start="#FFF" />
        </div>
      </RouterLink>
    </template>
    <template #content>
      <main
        class="h-full flex flex-col mx-auto max-w-[420px] relative"
        style="padding: 2rem 0; justify-content: space-between"
      >
        <div class="flex-1 flex items-center justify-center w-full min-h-0">
          <RouterView />
        </div>
        <!-- Powered by section at the bottom -->
        <div
          v-if="poweredByLogoUrl || poweredByCompany"
          class="w-full flex items-center justify-center gap-2"
          style="font-size: 0.875rem; color: #666; padding: 1.5rem 0 1rem 0; margin-top: auto"
        >
          <span v-if="poweredByLogoUrl || poweredByCompany" style="opacity: 0.7">Powered by</span>
          <img
            v-if="poweredByLogoUrl"
            :src="poweredByLogoUrl"
            alt="Powered by"
            style="height: 1.25rem; width: auto; max-width: 150px; object-fit: contain; opacity: 0.8"
            @error="handlePoweredByError"
          />
          <span v-if="poweredByCompany && !poweredByLogoUrl" style="font-weight: 500; opacity: 0.8">{{
            poweredByCompany
          }}</span>
          <span v-if="poweredByLogoUrl || poweredByCompany" style="opacity: 0.7">© {{ currentYear }}</span>
        </div>
      </main>
    </template>
  </VaLayout>

  <VaLayout v-else class="h-screen bg-[var(--va-background-secondary)]">
    <template #content>
      <div class="p-4">
        <main class="h-full flex flex-col items-center justify-start mx-auto max-w-[420px]">
          <div class="flex flex-col items-start flex-1 w-full">
            <!-- Only show logo if no banner is present (mobile view) -->
            <RouterLink
              v-if="!loginBannerUrl"
              class="py-4 w-full flex justify-center"
              to="/"
              aria-label="Visit homepage"
            >
              <VuesticLogo class="mb-2" height="112" />
            </RouterLink>
            <div class="flex-1 w-full">
              <RouterView />
            </div>
          </div>
          <!-- Powered by section at the bottom (mobile view) -->
          <div
            v-if="poweredByLogoUrl || poweredByCompany"
            class="w-full flex items-center justify-center gap-2 mt-4"
            style="font-size: 0.875rem; color: #666; padding: 1rem 0"
          >
            <span v-if="poweredByLogoUrl || poweredByCompany">Powered by</span>
            <img
              v-if="poweredByLogoUrl"
              :src="poweredByLogoUrl"
              alt="Powered by"
              style="height: 1.25rem; width: auto; max-width: 150px; object-fit: contain"
              @error="handlePoweredByError"
            />
            <span v-if="poweredByCompany && !poweredByLogoUrl" style="font-weight: 500">{{ poweredByCompany }}</span>
            <span v-if="poweredByLogoUrl || poweredByCompany">© {{ currentYear }}</span>
          </div>
        </main>
      </div>
    </template>
  </VaLayout>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useBreakpoint } from 'vuestic-ui'
import VuesticLogo from '../components/VuesticLogo.vue'
import { useSystemSettings } from '@/composables/useSystemSettings'
import { NAVBAR_YELLOW } from '@/config/theme-colors'

const breakpoint = useBreakpoint()
const { getLoginBannerUrl, getPoweredByLogoUrl, fetchSystemSettings, systemSettings } = useSystemSettings()
const loginBannerUrl = ref<string | null>(null)
const poweredByLogoUrl = ref<string | null>(null)
const poweredByCompany = ref<string>('')
const currentYear = new Date().getFullYear()

const leftPanelStyle = computed(() => {
  const baseStyle = {
    width: '35vw',
    borderRight: '3px solid #a3a3a3',
    height: '100vh',
    minHeight: '100vh',
  }

  if (loginBannerUrl.value) {
    return {
      ...baseStyle,
      backgroundColor: 'transparent',
    }
  }

  return {
    ...baseStyle,
    backgroundColor: NAVBAR_YELLOW,
  }
})

const handleBannerError = () => {
  // Fallback to default background if banner fails to load
  loginBannerUrl.value = null
}

const handlePoweredByError = () => {
  // Hide powered by logo if it fails to load
  poweredByLogoUrl.value = null
}

onMounted(async () => {
  // Fetch system settings and update banner
  await fetchSystemSettings()
  const bannerUrl = getLoginBannerUrl()
  console.log('Login banner URL:', bannerUrl)
  loginBannerUrl.value = bannerUrl

  // Get powered by logo URL
  const poweredByUrl = getPoweredByLogoUrl()
  console.log('Powered by logo URL:', poweredByUrl)
  poweredByLogoUrl.value = poweredByUrl

  // Get powered by company name
  if (systemSettings.value?.poweredByCompany) {
    poweredByCompany.value = systemSettings.value.poweredByCompany
    console.log('Powered by company:', poweredByCompany.value)
  }
})

// Watch for system settings changes and update banner
watch(
  () => systemSettings.value?.loginBannerUrl,
  (newUrl) => {
    if (newUrl) {
      const bannerUrl = getLoginBannerUrl()
      console.log('Login banner URL updated:', bannerUrl)
      loginBannerUrl.value = bannerUrl
    } else {
      loginBannerUrl.value = null
    }
  },
  { immediate: true },
)

// Watch for powered by logo changes
watch(
  () => systemSettings.value?.poweredByLogoUrl,
  (newUrl) => {
    if (newUrl) {
      const poweredByUrl = getPoweredByLogoUrl()
      poweredByLogoUrl.value = poweredByUrl
    } else {
      poweredByLogoUrl.value = null
    }
  },
  { immediate: true },
)

// Watch for powered by company changes
watch(
  () => systemSettings.value?.poweredByCompany,
  (newCompany) => {
    poweredByCompany.value = newCompany || ''
  },
  { immediate: true },
)
</script>
